import dayjs from 'dayjs';
import { AdminAlert, AdminAlertType } from '../../types/adminAlert';

export const formatRelativeTime = (iso: string): string => {
    const now = dayjs();
    const target = dayjs(iso);
    const diffSec = now.diff(target, 'second');
    if (diffSec < 60) return '방금 전';
    const diffMin = now.diff(target, 'minute');
    if (diffMin < 60) return `${diffMin}분 전`;
    const diffHour = now.diff(target, 'hour');
    if (diffHour < 24) return `${diffHour}시간 전`;
    const diffDay = now.diff(target, 'day');
    if (diffDay === 1) return '어제';
    if (diffDay < 7) return `${diffDay}일 전`;
    return target.format('YYYY.MM.DD');
};

export const getAlertAccentColor = (type: AdminAlertType): string => {
    switch (type) {
        case 'NEW_OPPORTUNITY':
            return '#2563EB';
        case 'KIOSK_TRAINER_PROMO_LEAD':
            return '#0EA5E9';
        case 'COMM_BOX_NEW':
        case 'COMM_BOX_REPLY':
            return '#7C3AED';
        case 'PAYMENT_FAIL':
            return '#DC2626';
        case 'BAD_REVIEW':
            return '#F59E0B';
        case 'SCHEDULE_NO_SHOW':
            return '#EA580C';
        case 'SESSION_REPORT_SUPPORT_RESOLVED':
            return '#15803D';
        default:
            return '#64748B';
    }
};

export const getAlertTypeLabel = (type: AdminAlertType): string => {
    switch (type) {
        case 'NEW_OPPORTUNITY':
            return '신규기회';
        case 'KIOSK_TRAINER_PROMO_LEAD':
            return '강사 광고';
        case 'COMM_BOX_NEW':
            return '소통상자';
        case 'COMM_BOX_REPLY':
            return '소통상자';
        case 'PAYMENT_FAIL':
            return '결제';
        case 'BAD_REVIEW':
            return '리뷰';
        case 'SCHEDULE_NO_SHOW':
            return '노쇼';
        case 'SESSION_REPORT_SUPPORT_RESOLVED':
            return '라포 확인';
        default:
            return '알림';
    }
};

/**
 * 노쇼 카드 → 스케줄 캘린더의 그 날짜 일간 뷰.
 * 일정 등록·수정 후 돌아올 때 쓰는 기존 state 규약(calendarType + calendarTimeInfo)을 그대로 쓴다
 * (SchedulePage 가 그 날짜를 고르고 수업 시각으로 스크롤한다). PT·그룹 모두 같은 캘린더에 뜬다.
 * 날짜 좌표가 없으면 state 없이 캘린더만 연다.
 */
export const buildNoShowScheduleRoute = (
    alert: Pick<AdminAlert, 'metadata'>
): { pathname: string; state?: { calendarType: 'daily'; calendarTimeInfo: string } } => {
    const timeInfo = alert.metadata?.startTime || alert.metadata?.scheduleDate || null;
    if (!timeInfo || !dayjs(timeInfo).isValid()) return { pathname: '/schedule' };
    return { pathname: '/schedule', state: { calendarType: 'daily', calendarTimeInfo: timeInfo } };
};

/** 세션 현황으로 들어가며 그 달·그 강사를 고르게 하는 이동 state. SettlementPage 가 읽는다. */
export interface SessionReportFocusState {
    sessionReportFocus: { yearMonth: string; coachId?: string };
}

const isValidYearMonth = (value: unknown): value is string =>
    typeof value === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);

/**
 * 라포 확인 카드 → 정산 > 세션 현황 탭.
 * metadata.yearMonth 가 올바르면 그 달로, coachId 가 있으면 그 강사까지 고른다.
 * metadata 가 비거나 이상하면 state 없이 세션 현황 탭만 연다.
 */
export const buildSessionReportSupportRoute = (
    alert: Pick<AdminAlert, 'metadata'>
): { pathname: string; search: string; state?: SessionReportFocusState } => {
    const base = { pathname: '/settlement', search: '?tab=sessionReport' };
    const metadata = alert?.metadata;
    if (!metadata || typeof metadata !== 'object' || !isValidYearMonth(metadata.yearMonth)) return base;
    const coachId = typeof metadata.coachId === 'string' && metadata.coachId.trim() ? metadata.coachId.trim() : undefined;
    return { ...base, state: { sessionReportFocus: { yearMonth: metadata.yearMonth, ...(coachId ? { coachId } : {}) } } };
};
